import { useEffect, useRef, useState } from "react";
import { adminApi } from "@/api/admin.api";

type Target = "central" | "byor" | "applied" | "resumes_v2";
type Mode = "dry_run" | "write";

interface DatesBackfillProgress {
  jobId: string;
  target: Target;
  mode: Mode;
  organizationId?: string;
  limit?: number;
  status: "RUNNING" | "COMPLETED" | "FAILED";
  indexCount: number;
  processedDocs: number;
  alreadyDoneDocs: number;
  changedDocs: number;
  conflictDocs: number;
  failedDocs: number;
  jobs: number;
  datedJobs: number;
  alreadyStructuredJobs: number;
  yearOnlyJobs: number;
  currentJobs: number;
  endMissingJobs: number;
  endInferredJobs: number;
  unreadableJobs: number;
  yearsChangedDocs: number;
  yearsMaxChange: number;
  yearsSample: Array<{ index: string; id: string; before: number | null; after: number }>;
  currentIndex?: string;
  error?: string;
  startedAt: string;
  finishedAt?: string;
}

interface DatesCoverage {
  target: Target;
  label: string;
  indexPattern: string;
  indexCount: number;
  totalDocs: number;
  doneDocs: number;
}

const TARGETS: Array<{ value: Target; label: string; live: boolean }> = [
  { value: "central", label: "Resume Matches uploads (*_central_resumes)", live: false },
  { value: "byor", label: "Upload New Resumes (*_resumes)", live: false },
  {
    value: "applied",
    label: "Applicants (*_applied_candidates, shared with theResume)",
    live: true,
  },
  { value: "resumes_v2", label: "Talent Pool (resumes_v2, shared with theResume)", live: true },
];
const POLL_INTERVAL_MS = 2000;
const n = (v: number) => v.toLocaleString();

/**
 * Phase 3 of the experience dates work: structured month/year dates on every stored resume, and the
 * stored years of experience worked out again. Dry run first; writes skip documents changed while
 * they run, and re-runs skip finished work.
 */
export function ExperienceDatesBackfill() {
  const [coverage, setCoverage] = useState<DatesCoverage[]>([]);
  const [active, setActive] = useState<DatesBackfillProgress | null>(null);
  const [recent, setRecent] = useState<DatesBackfillProgress[]>([]);
  const [target, setTarget] = useState<Target>("central");
  const [mode, setMode] = useState<Mode>("dry_run");
  const [limit, setLimit] = useState("");
  const [confirmText, setConfirmText] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [starting, setStarting] = useState(false);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const refresh = async () => {
    try {
      const [{ active: a }, { jobs }, { coverage: cov }] = await Promise.all([
        adminApi.getActiveExperienceDatesBackfill(),
        adminApi.getRecentExperienceDatesBackfills(),
        adminApi.getExperienceDatesBackfillCoverage(),
      ]);
      setActive(a);
      setRecent(jobs);
      setCoverage(cov || []);
      return a as DatesBackfillProgress | null;
    } catch (e: any) {
      setError(e.message || "Failed to load the dates backfill status");
      return null;
    }
  };

  useEffect(() => {
    refresh();
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (active?.status === "RUNNING" && !pollRef.current) {
      pollRef.current = setInterval(async () => {
        const a = await refresh();
        if (a?.status !== "RUNNING" && pollRef.current) {
          clearInterval(pollRef.current);
          pollRef.current = null;
        }
      }, POLL_INTERVAL_MS);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active?.status]);

  const running = active?.status === "RUNNING";
  const writing = mode === "write";
  const limitNumber = limit.trim() ? Number(limit) : undefined;
  const limitInvalid =
    limitNumber !== undefined && (!Number.isInteger(limitNumber) || limitNumber < 1);
  const canStart =
    !running && !starting && !limitInvalid && (!writing || confirmText === "CONFIRM");

  const start = async () => {
    setStarting(true);
    setError(null);
    try {
      await adminApi.startExperienceDatesBackfill({
        target,
        mode,
        organizationId: target === "central" ? "all" : undefined,
        limit: writing ? limitNumber : undefined,
        confirm: writing ? true : undefined,
      });
      setConfirmText("");
      await refresh();
    } catch (e: any) {
      setError(e.message || "Failed to start the dates backfill");
    } finally {
      setStarting(false);
    }
  };

  const shown = active ?? recent[0] ?? null;

  return (
    <div>
      <h1 className="text-xl font-semibold mb-1">Experience Dates Backfill</h1>
      <p className="text-sm text-gray-500 mb-6">
        Adds structured dates (month and year, or "currently works here") to every job on stored
        resumes, read from their date text with the same reader as theResume. Uploaded resumes also
        get their stored years of experience worked out again, which the experience filter uses. No
        AI, no re-parse, no re-embedding; nothing else changes. Run a dry run first: it writes
        nothing and reports what would change.
      </p>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-6">
          {error}
        </div>
      )}

      {coverage.length > 0 && (
        <table className="w-full text-sm text-left border rounded overflow-hidden mb-8">
          <thead className="bg-gray-100">
            <tr>
              <th className="p-3">Target</th>
              <th className="p-3">Indexes</th>
              <th className="p-3">Documents</th>
              <th className="p-3">Dates done</th>
            </tr>
          </thead>
          <tbody>
            {coverage.map((c) => (
              <tr key={c.target} className="border-t">
                <td className="p-3">
                  <div className="font-medium">{c.label}</div>
                  <code className="text-xs text-gray-500">{c.indexPattern}</code>
                </td>
                <td className="p-3">{c.indexCount}</td>
                <td className="p-3">{n(c.totalDocs)}</td>
                <td className="p-3">
                  {n(c.doneDocs)}
                  {c.totalDocs > 0 && (
                    <span className="text-xs text-gray-400">
                      {" "}
                      ({Math.round((100 * c.doneDocs) / c.totalDocs)}%)
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <div className="border rounded-lg p-5 mb-8 grid gap-4 md:grid-cols-3">
        <label className="block">
          <span className="block text-xs uppercase text-gray-500 mb-1">Target</span>
          <select
            className="w-full border rounded p-2 text-sm"
            value={target}
            onChange={(e) => setTarget(e.target.value as Target)}
            disabled={running}
          >
            {TARGETS.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </select>
        </label>
        <fieldset>
          <legend className="block text-xs uppercase text-gray-500 mb-1">Mode</legend>
          <label className="mr-4 text-sm">
            <input
              type="radio"
              checked={mode === "dry_run"}
              onChange={() => setMode("dry_run")}
              disabled={running}
            />{" "}
            Dry run (writes nothing)
          </label>
          <label className="text-sm">
            <input
              type="radio"
              checked={mode === "write"}
              onChange={() => setMode("write")}
              disabled={running}
            />{" "}
            Write
          </label>
        </fieldset>
        <label className="block">
          <span className="block text-xs uppercase text-gray-500 mb-1">
            Stop after (write only, optional)
          </span>
          <input
            className="w-full border rounded p-2 text-sm"
            value={limit}
            onChange={(e) => setLimit(e.target.value)}
            placeholder="e.g. 50 for a trial"
            disabled={running || !writing}
            inputMode="numeric"
          />
          {limitInvalid && <span className="text-xs text-red-600">A whole number above 0</span>}
        </label>
        {writing && (
          <div className="md:col-span-3 border border-amber-400 bg-amber-50/60 rounded p-3">
            <p className="text-sm text-gray-700 mb-2">
              This updates documents{" "}
              {TARGETS.find((t) => t.value === target)?.live ? (
                <strong>in an index theResume serves live</strong>
              ) : (
                "in theRecruit indexes"
              )}
              . A document changed while the job runs is skipped, not overwritten; re-running picks
              it up. Type <strong>CONFIRM</strong> to proceed.
            </p>
            <input
              className="w-full border rounded p-2 text-sm"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder="CONFIRM"
            />
          </div>
        )}
        <div className="md:col-span-3">
          <button
            className={`${
              writing ? "bg-amber-600 hover:bg-amber-700" : "bg-blue-600 hover:bg-blue-700"
            } text-white px-4 py-2 rounded disabled:opacity-50`}
            onClick={start}
            disabled={!canStart}
          >
            {running ? "A job is running…" : writing ? "Start write" : "Start dry run"}
          </button>
        </div>
      </div>

      {shown && (
        <div className="border rounded-lg p-5 mb-8">
          <div className="flex flex-wrap items-baseline justify-between gap-2 mb-3">
            <h2 className="font-semibold">
              {shown.mode === "dry_run" ? "Dry run" : "Write"} ·{" "}
              {TARGETS.find((t) => t.value === shown.target)?.label}
              {shown.limit ? ` · stop after ${n(shown.limit)}` : ""}
            </h2>
            <span
              className={`text-sm font-medium ${
                shown.status === "FAILED"
                  ? "text-red-600"
                  : shown.status === "RUNNING"
                  ? "text-blue-600"
                  : "text-green-700"
              }`}
            >
              {shown.status === "RUNNING"
                ? `Running${shown.currentIndex ? ` · ${shown.currentIndex}` : ""}`
                : shown.status}
            </span>
          </div>
          {shown.error && <p className="text-sm text-red-600 mb-3">{shown.error}</p>}
          <dl className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-2 text-sm">
            <Stat label="Indexes" value={shown.indexCount} />
            <Stat label="Documents looked at" value={shown.processedDocs} />
            <Stat
              label={shown.mode === "dry_run" ? "Would change" : "Changed"}
              value={shown.changedDocs}
            />
            {shown.mode === "dry_run" ? (
              <Stat label="Already done" value={shown.alreadyDoneDocs} />
            ) : (
              <Stat
                label="Skipped (changed during run)"
                value={shown.conflictDocs}
                warn={shown.conflictDocs > 0}
              />
            )}
            <Stat label="Failed" value={shown.failedDocs} warn={shown.failedDocs > 0} />
            <Stat label="Jobs" value={shown.jobs} />
            <Stat label="Jobs with dates" value={shown.datedJobs} />
            <Stat label="Already structured" value={shown.alreadyStructuredJobs} />
            <Stat label="Year only (no month)" value={shown.yearOnlyJobs} />
            <Stat label="Current" value={shown.currentJobs} />
            <Stat label="Latest job, no end date" value={shown.endMissingJobs} />
            <Stat label="Earlier job, end inferred" value={shown.endInferredJobs} />
            <Stat
              label="Date text unreadable"
              value={shown.unreadableJobs}
              warn={shown.unreadableJobs > 0}
            />
            {shown.target === "central" && (
              <>
                <Stat label="Stored years that change" value={shown.yearsChangedDocs} />
                <Stat label="Largest years change" value={shown.yearsMaxChange} />
              </>
            )}
          </dl>
          {shown.yearsSample?.length > 0 && (
            <p className="text-xs text-gray-500 mt-3">
              Largest years changes:{" "}
              {shown.yearsSample.map((s) => `${s.before ?? "none"} → ${s.after}`).join(", ")}
            </p>
          )}
        </div>
      )}

      {recent.length > 0 && (
        <table className="w-full text-sm text-left border rounded overflow-hidden">
          <thead className="bg-gray-100">
            <tr>
              <th className="p-3">Started</th>
              <th className="p-3">Mode</th>
              <th className="p-3">Target</th>
              <th className="p-3">Looked at</th>
              <th className="p-3">Changed</th>
              <th className="p-3">Skipped</th>
              <th className="p-3">Failed</th>
              <th className="p-3">Status</th>
            </tr>
          </thead>
          <tbody>
            {recent.map((j) => (
              <tr key={j.jobId} className="border-t">
                <td className="p-3">{new Date(j.startedAt).toLocaleString()}</td>
                <td className="p-3">{j.mode === "dry_run" ? "Dry run" : "Write"}</td>
                <td className="p-3">{j.target}</td>
                <td className="p-3">{n(j.processedDocs)}</td>
                <td className="p-3">{n(j.changedDocs)}</td>
                <td className="p-3">{n(j.conflictDocs)}</td>
                <td className="p-3">{n(j.failedDocs)}</td>
                <td className="p-3">{j.status}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function Stat({ label, value, warn }: { label: string; value: number; warn?: boolean }) {
  return (
    <div>
      <dt className="text-xs text-gray-500">{label}</dt>
      <dd className={`font-medium ${warn ? "text-amber-700" : ""}`}>{n(value ?? 0)}</dd>
    </div>
  );
}
